import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { unitOfMeasureSchema, IUnitOfMeasure } from './unitOfMeasure';
import { IUnitOfMeasureType } from '../unitOfMeasureType/unitOfMeasureType';

/**
 * Schema for validating unit of measure form data.
 * Extends the base unit of measure schema with form-specific validations.
 *
 * @property {string} [code] - Unit of measure code (max 5 characters)
 * @property {string} [name] - Unit of measure name (max 255 characters)
 * @property {OptionType} [unitOfMeasureType] - Unit of measure type option with label/value pair
 */
export const unitOfMeasureFormSchema = unitOfMeasureSchema.extend({
    unitOfMeasureType: optionTypeSchema.refine(val => val !== undefined, { message: 'Required' }),
}).omit({
    // Omit the original ID field
    typeId: true,
});

/**
 * Default schema for unit of measure form initialization.
 * Extends the form schema by making required fields optional with defaults.
 *
 * @property {string} [code] - Optional unit of measure code
 * @property {string} [name] - Optional unit of measure name
 * @property {OptionType} [unitOfMeasureType] - Optional unit of measure type option with label/value pair
 */
export const unitOfMeasureDefaultFormSchema = unitOfMeasureFormSchema.extend({
    code: z.string().optional().default(''),
    name: z.string().optional().default(''),
    unitOfMeasureType: optionTypeSchema.optional(),
});

/**
 * TypeScript interface for unit of measure default form values.
 * Generated from the unit of measure default form schema for type safety.
 */
export type IUnitOfMeasureDefaultFormValues = z.infer<typeof unitOfMeasureDefaultFormSchema>;


/**
 * TypeScript interface for unit of measure form values.
 * Generated from the unit of measure form schema for type safety.
 */
export type IUnitOfMeasureFormValues = z.infer<typeof unitOfMeasureFormSchema>;

/**
 * Converts a Unit of Measure model to form values for editing.
 * If no unit of measure is provided, returns default form values.
 *
 * @param {IUnitOfMeasure} [unitOfMeasure] - Optional unit of measure model to convert
 * @param {Array<IUnitOfMeasureType>} [unitOfMeasureTypes] - Array of unit of measure types to use for mapping
 * @returns {IUnitOfMeasureFormValues} Form values ready for editing
 */
export const UnitOfMeasureFormValues = (
    unitOfMeasure ?: IUnitOfMeasure,
    unitOfMeasureTypes ?: Array<IUnitOfMeasureType>
) : IUnitOfMeasureDefaultFormValues | IUnitOfMeasureFormValues => {
    // Handle the default case (new unit of measure)
    if (!unitOfMeasure) {
        return unitOfMeasureDefaultFormSchema.parse({});
    }

    const { typeId, ...rest } = unitOfMeasure;

    // Transform the IUnitOfMeasure data into IUnitOfMeasureFormValues structure
    return unitOfMeasureFormSchema.parse({
        ...rest,
        unitOfMeasureType: typeId
            ? {
                label: unitOfMeasureTypes?.find(x => x.id === typeId)?.name ?? '',
                value: typeId,
            }
            : undefined,
    });
};

/**
 * Validates unit of measure form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IUnitOfMeasureFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validateUnitOfMeasureForm = (formValues : IUnitOfMeasureFormValues) : boolean => {
    try {
        unitOfMeasureFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Unit of measure form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
